import React, { useCallback, useRef, useMemo } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { Button } from 'ui-components/components/button';
import { noop } from 'ui-components/default-props/noop';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { InputClearIcon } from '../../../advanced-search/icons/input-clear-icon';
import { InputSearchIcon } from '../../../advanced-search/icons/input-search-icon';
import style from '../../../advanced-search/search-form.module.less';
import { pushToHistoryWithSearchTerm } from '../../../services/quicksearch-location-service';
import QuickSearchFocusHotkey from '../../hotkeys/quicksearch-focus';

const stopPropagation = (e) => {
  if (e && e.stopPropagation) {
    e.stopPropagation();
  }
};

/**
 * @typedef {object} SearchFormProps
 * @property {string} [searchInputValue]
 * @property {(data: { searchTerm: string }) => void} [onSearch]
 * @property {(value: string) => void} handleSearchInputChange
 * @property {boolean} isHighlighted
 * @property {() => void} clearSearchInput
 * @property {() => void} [resetGrid]
 * @property {boolean} [isDisabled]
 * @property {object} [ariaLabelMessageDescriptor]
 * @property {object} [formTitleMessageDescriptor]
 * @property {object} [placeholderMessageDescriptor]
 */

/**
 * @param {SearchFormProps} props
 * @returns {React.JSX.Element}
 */
export const SearchForm = ({
  searchInputValue,
  onSearch = noop,
  handleSearchInputChange,
  isHighlighted,
  clearSearchInput,
  resetGrid = noop,
  isDisabled = false,
  ariaLabelMessageDescriptor,
  formTitleMessageDescriptor,
  placeholderMessageDescriptor,
}) => {
  const intl = useIntl();
  const inputRef = useRef();
  const forceFocusSearchInput = useCallback(() => inputRef.current?.focus(), [inputRef]);

  const hasSearchInputValue = useMemo(() => !!searchInputValue?.length, [searchInputValue]);
  const history = useHistory();

  const handleQuickSearch = useCallback(
    (e) => {
      e?.preventDefault();

      if (hasSearchInputValue) {
        const trimmedSearchTerm = searchInputValue.trim();
        pushToHistoryWithSearchTerm(history, trimmedSearchTerm);
        onSearch({ searchTerm: trimmedSearchTerm });
      }
    },
    [hasSearchInputValue, searchInputValue, onSearch, history],
  );

  const handleClearInput = useCallback(() => {
    clearSearchInput();
    resetGrid();
    pushToHistoryWithSearchTerm(history);
  }, [clearSearchInput, resetGrid, history]);

  const handleSearchTermChange = useCallback(
    (value) => {
      if (value.length === 0) {
        handleClearInput();
      }
    },
    [handleClearInput],
  );

  const handleChange = useCallback(
    (event) => {
      const fieldValue = event.target.value;
      const fieldName = event.target.name;

      handleSearchInputChange(fieldValue);

      if (fieldName === 'searchTerm') {
        handleSearchTermChange(fieldValue);
      }
    },
    [handleSearchInputChange, handleSearchTermChange],
  );

  const wrapperClassName = classnames(style['search-box-wrapper'], {
    [style.highlighted]: isHighlighted,
  });

  const xmarkClassName = classnames(style['icon-xmark'], {
    [style.highlighted]: isHighlighted,
  });

  const ariaLabel = ariaLabelMessageDescriptor ? intl.formatMessage(ariaLabelMessageDescriptor) : null;
  const formTitle = formTitleMessageDescriptor ? intl.formatMessage(formTitleMessageDescriptor) : null;
  const placeholder = placeholderMessageDescriptor ? intl.formatMessage(placeholderMessageDescriptor) : null;

  return (
    <form onSubmit={handleQuickSearch} title={formTitle}>
      <div className={style['search-area']}>
        <div className={style['search-expander']}>
          <div className={wrapperClassName} onClick={stopPropagation}>
            <QuickSearchFocusHotkey onFocusQuickSearch={forceFocusSearchInput}>
              <input
                ref={inputRef}
                aria-label={ariaLabel}
                className={style['search-input']}
                disabled={isDisabled}
                name='searchTerm'
                onChange={handleChange}
                placeholder={placeholder}
                type='text'
                value={searchInputValue || ''}
              />
            </QuickSearchFocusHotkey>
            {hasSearchInputValue ? (
              <Button className={style['cancel-button']} disabled={isDisabled} onClick={handleClearInput} theme={THEME_CONSTANTS.unstyled}>
                <InputClearIcon className={xmarkClassName} />
              </Button>
            ) : null}
            <Button className={style['search-button']} disabled={isDisabled} onClick={handleQuickSearch} theme={THEME_CONSTANTS.unstyled}>
              <InputSearchIcon className={style['search-icon']} />
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};
